import { useCallback, useRef } from 'react';

export type StickyDirection = 'bottom' | 'left' | 'right' | 'top';

const STICKY_DIRECTION_TO_ROOT_MARGIN_MAPPING: {
  [key in StickyDirection]: string;
} = {
  bottom: '0px 0px -1px 0px',
  left: '0px 0px 0px -1px',
  right: '0px -1px 0px 0px',
  top: '-1px 0px 0px 0px',
};

interface UseToggleStuckClassRefArgs {
  enabled?: boolean;
  stuckClassName?: string;
  direction?: StickyDirection;
}

/**
 * Custom hook that observes an element's sticky state and toggles a specified class accordingly.
 */
const useToggleStuckClassRef = ({
  enabled = true,
  stuckClassName = 'is-stuck',
  direction = 'top',
}: UseToggleStuckClassRefArgs = {}) => {
  const observerRef = useRef<IntersectionObserver>();

  return useCallback(
    (stickyElement: HTMLElement | null) => {
      const cleanup = () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = undefined;
        }
      };

      if (!stickyElement) {
        return cleanup;
      }

      if (!enabled) {
        stickyElement.classList.remove(stuckClassName);
        return cleanup;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          if (entry.intersectionRatio < 1) {
            stickyElement.classList.add(stuckClassName);
          } else {
            stickyElement.classList.remove(stuckClassName);
          }
        },
        {
          threshold: [1],
          rootMargin: STICKY_DIRECTION_TO_ROOT_MARGIN_MAPPING[direction],
        },
      );

      observer.observe(stickyElement);
      observerRef.current = observer;

      return cleanup;
    },
    [enabled, stuckClassName, direction],
  );
};

export default useToggleStuckClassRef;
